import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setFileDlpModalState } from '../../Store/Actions/modalActions';
import useSocketMessageHandler from '../../Hooks/useSocketMessageHandler';
import { SocketContext } from '../../SocketContext';
import { FileDlpMessage } from '../../Types';
import { Button } from '../Utility/Button';
import { Check, Info, Warning } from '@phosphor-icons/react';
import { Badge } from '../Utility/Badge';
import Select from '../Utility/FormElements/Select';
import { FileDlpModalDataOrigin } from '../../Types/StoreTypes';
import { DLPState } from '../../Types/Enums';

interface FileStatus {
    sha1: string;
    status: 'starting' | 'finished' | 'content_extracted' | 'dlp_check_started' | 'error';
    name: string;
    countByTypes?: Record<string, number>;
    selected?: 'use' | 'skip';
}

const FileDlpModal: React.FC = () => {
    const dispatch = useDispatch();
    const { fileDlpModalState } = useSelector((state: any) => state.modal);
    const i18n = useSelector((state: any) => state.i18n.i18n);
    const { SocketMessageHandlerActions } = useSocketMessageHandler();
    const { addMessageHandler } = useContext(SocketContext);
    const [fileStatuses, setFileStatuses] = useState<FileStatus[]>([]);
    const [fileDuplicates, setFileDuplicates] = useState<{ [key: string]: FileStatus[] }>({});
    const tmp =
        fileDlpModalState.origin !== FileDlpModalDataOrigin.Knowledgebase
            ? {}
            : fileStatuses.reduce<{ [key: string]: FileStatus[] }>(
                  (acc, file) => ({ ...acc, [file.sha1]: (acc[file.sha1] ?? []).concat([file]) }),
                  {},
              );
    const fileDuplicatesTmp = Object.keys(tmp).reduce<{ [key: string]: FileStatus[] }>(
        (acc, sha1) => (tmp[sha1].length === 1 ? acc : { ...acc, [sha1]: tmp[sha1] }),
        {},
    );

    if (Object.keys(fileDuplicatesTmp).length > 0) {
        const uniqueFileStatuses = fileStatuses.filter(
            (file) => fileDuplicatesTmp[file.sha1] === undefined || fileDuplicatesTmp[file.sha1].indexOf(file) === 0,
        );
        setFileStatuses(uniqueFileStatuses);
        setFileDuplicates(fileDuplicatesTmp);
    }

    useEffect(() => {
        if (fileDlpModalState.files.length > 0) {
            // Skip DLP check and auto-approve all files if DLP is disabled
            if (fileDlpModalState.combinedDlp === DLPState.dlp_off) {
                // Call callback immediately with all files
                if (fileDlpModalState.callback) {
                    fileDlpModalState.callback(fileDlpModalState.files);
                }
                // Close modal immediately
                dispatch(setFileDlpModalState({ ...fileDlpModalState, state: 'closed', files: [] }));
                return;
            }

            const initialStatuses = fileDlpModalState.files.map((file: any) => ({
                sha1: file.sha1,
                status: 'starting' as const,
                name: file.name,
                countByTypes: {},
                selected: 'skip',
            }));
            setFileDuplicates({});
            setFileStatuses(initialStatuses);
            SocketMessageHandlerActions.checkFiles(fileDlpModalState.files.map((file: any) => file.sha1));
        }
    }, [fileDlpModalState, SocketMessageHandlerActions, dispatch]);

    const handleClose = () => {
        const selectedFiles = fileStatuses
            .filter((file) => file.selected === 'use')
            .map((file) => fileDlpModalState.files.find((f: any) => f.sha1 === file.sha1))
            .filter(Boolean);
        fileDlpModalState.callback && fileDlpModalState.callback(selectedFiles);
        dispatch(setFileDlpModalState({ ...fileDlpModalState, state: 'closed', files: [] }));
    };

    const handleConfirm = () => {
        const selectedFiles = fileStatuses
            .filter((file) => file.selected === 'use')
            .map((file) => fileDlpModalState.files.find((f: any) => f.sha1 === file.sha1))
            .filter(Boolean);
        fileDlpModalState.callback && fileDlpModalState.callback(selectedFiles);
        dispatch(setFileDlpModalState({ ...fileDlpModalState, state: 'closed', files: [] }));
    };

    const handleFileSelection = (sha1: string, selection: 'use' | 'skip') => {
        setFileStatuses((prevStatuses) =>
            prevStatuses.map((status) => (status.sha1 === sha1 ? { ...status, selected: selection } : status)),
        );
    };

    useEffect(() => {
        addMessageHandler('fileDlp', (data: FileDlpMessage) => {
            if (data.direction === 'toClient') {
                setFileStatuses((prevStatuses) =>
                    prevStatuses.map((status) =>
                        status.sha1 === data.data.sha1File
                            ? {
                                  ...status,
                                  status: data.data.status as
                                      | 'starting'
                                      | 'finished'
                                      | 'content_extracted'
                                      | 'dlp_check_started'
                                      | 'error',
                                  countByTypes: data.data.countByTypes || {},
                                  selected:
                                      data.data.status === 'finished'
                                          ? data.data.countByTypes && Object.keys(data.data.countByTypes).length > 0
                                              ? 'skip'
                                              : 'use'
                                          : status.selected,
                              }
                            : status,
                    ),
                );
            }
        });
    }, [addMessageHandler, dispatch]);

    const getStatusText = (status: string) => {
        switch (status) {
            case 'starting':
                return i18n.translate({ id: 'checking', sourceName: 'FileDlpModal' });
            case 'finished':
                return i18n.translate({ id: 'check_completed', sourceName: 'FileDlpModal' });
            case 'error':
                return i18n.translate({ id: 'check_error', sourceName: 'FileDlpModal' });
            case 'content_extracted':
                return i18n.translate({ id: 'content_extracted', sourceName: 'FileDlpModal' });
            case 'dlp_check_started':
                return i18n.translate({ id: 'dlp_check_started', sourceName: 'FileDlpModal' });
            default:
                return status;
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'starting':
                return 'text-yellow-500';
            case 'finished':
                return '';
            case 'error':
                return 'text-red-500';
            default:
                return 'text-gray-500';
        }
    };

    const getFoundTypesTotalCount = (countByTypes: Record<string, number> | undefined) => {
        if (!countByTypes || Object.keys(countByTypes).length === 0) {
            return 0;
        }
        return Object.values(countByTypes).reduce((sum, count) => sum + count, 0);
    };

    const getFoundTypesText = (countByTypes: Record<string, number> | undefined) => {
        if (!countByTypes || Object.keys(countByTypes).length === 0) {
            return i18n.translate({ id: 'no_sensitive_data', sourceName: 'FileDlpModal' });
        }

        const typesList = Object.entries(countByTypes)
            .map(
                ([type, count]) =>
                    `${i18n.translate({
                        id: `dlp.type.${type.toLowerCase()}`,
                        sourceName: 'FileDlpModal',
                    })} (${count})`,
            )
            .join(', ');
        return `${typesList}`;
    };

    const areAllFilesProcessed = () => {
        return (
            fileStatuses.length > 0 &&
            fileStatuses.every((file) => file.status === 'finished' || file.status === 'error')
        );
    };

    const dropDown = (file: FileStatus) => {
        return (
            <>
                <label htmlFor={`file-selection-${file.sha1}`} className="sr-only">
                    {i18n.translate({
                        id: 'document_selection_for',
                        sourceName: 'FileDlpModal',
                    })}{' '}
                    {file.name}
                </label>
                <Select
                    id={`file-selection-${file.sha1}`}
                    value={file.selected}
                    onChange={(value) => handleFileSelection(file.sha1, value as 'use' | 'skip')}
                >
                    {fileDlpModalState.combinedDlp === DLPState.dlp_optional && (
                        <option value="use">
                            {i18n.translate({
                                id: 'use_original_document',
                                sourceName: 'FileDlpModal',
                            })}
                        </option>
                    )}
                    <option value="encrypted" disabled>
                        {i18n.translate({
                            id: 'use_encrypted_version',
                            sourceName: 'FileDlpModal',
                        })}
                    </option>
                    <option value="skip">
                        {i18n.translate({
                            id: 'do_not_use_document',
                            sourceName: 'FileDlpModal',
                        })}
                    </option>
                </Select>
            </>
        );
    };

    return fileDlpModalState.state === 'open' ? (
        <div
            className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[2]"
            onClick={() => {
                handleClose();
            }}
        >
            <div
                className={`bg-modalBackground rounded-lg py-8 mx-3 w-full relative max-w-4xl px-8`}
                onClick={(e) => e.stopPropagation()}
            >
                <h4>{i18n.translate({ id: 'dlp_check', sourceName: 'FileDlpModal' })}</h4>
                <p>{i18n.translate({ id: 'check_duration_info', sourceName: 'FileDlpModal' })}</p>
                <div className="max-h-[calc(var(--app-height)/2)] overflow-y-auto mt-3">
                    {Object.keys(fileDuplicates).length > 0 && (
                        <Badge variant="info" className="w-full mt-2">
                            <div className="flex items-start">
                                <div className="flex-shrink-0">
                                    <Info size={20} />
                                </div>
                                <div className="ml-2">
                                    <div className="text-sm font-bold">
                                        {i18n.translate({ id: 'duplicate.title', sourceName: 'FileDlpModal' })}
                                    </div>
                                    <div className="text-xs whitespace-pre-line">
                                        {i18n.translate({ id: 'duplicate.description', sourceName: 'FileDlpModal' })}
                                        <ul className="py-0">
                                            {Object.values(fileDuplicates)
                                                .map((files) =>
                                                    files.slice(1).map((file) => <li className="my-1">{file.name}</li>),
                                                )
                                                .flat()}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </Badge>
                    )}

                    {fileStatuses.length > 0 ? (
                        <div className="space-y-0 divide-y divide-layout-borderColor">
                            {fileStatuses.map((file) => (
                                <div key={file.sha1} className="flex flex-col py-4 overflow-hidden">
                                    <div className="mt-2 ">
                                        <div className="flex items-start gap-4">
                                            <div className="flex-1">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex-1">
                                                        <div className="font-medium text-base">{file.name}</div>
                                                        <div className="flex items-center gap-2 py-2">
                                                            {file.status === 'finished' && (
                                                                <div className="w-4 h-4 rounded-full bg-success flex items-center justify-center">
                                                                    <Check size={12} />
                                                                </div>
                                                            )}
                                                            <div className={`text-xs ${getStatusColor(file.status)}`}>
                                                                {getStatusText(file.status)}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex-1 w-full hidden md:flex justify-end">
                                                {file.status === 'finished' && dropDown(file)}
                                                {file.status === 'starting' && (
                                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
                                                )}
                                            </div>
                                        </div>
                                        {file.status === 'finished' &&
                                            file.countByTypes &&
                                            Object.keys(file.countByTypes).length > 0 && (
                                                <Badge variant="warning" className="w-full mt-2">
                                                    <div className="flex items-start">
                                                        <div className="flex-shrink-0">
                                                            <Warning size={20} />
                                                        </div>
                                                        <div className="ml-2">
                                                            <div className="text-sm font-bold">
                                                                {getFoundTypesTotalCount(file.countByTypes)}{' '}
                                                                {i18n.translate({
                                                                    id: 'sensitive_data_types_found',
                                                                    sourceName: 'FileDlpModal',
                                                                })}
                                                            </div>
                                                            <div className="text-xs mt-1 whitespace-pre-line">
                                                                {getFoundTypesText(file.countByTypes)}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </Badge>
                                            )}

                                        {file.status === 'finished' && (
                                            <div className="block md:hidden mt-3">{dropDown(file)}</div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="mt-8 flex justify-end">
                            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto"></div>
                        </div>
                    )}
                </div>
                <div className="mt-6 flex justify-end space-x-4">
                    <Button variant="secondary" onClick={handleClose}>
                        {i18n.translate({ id: 'cancel', sourceName: 'FileDlpModal' })}
                    </Button>
                    {areAllFilesProcessed() && (
                        <Button onClick={handleConfirm}>
                            {i18n.translate({ id: 'confirm', sourceName: 'FileDlpModal' })}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    ) : null;
};

export default FileDlpModal;
